import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerOverlay,
  HStack,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import CodeMirror, { EditorView } from "@uiw/react-codemirror";
import { html as HtmlLang } from "@codemirror/lang-html";
import { css as CssLang } from "@codemirror/lang-css";
import { javascript as JsLang } from "@codemirror/lang-javascript";
import { vscodeDark } from "@uiw/codemirror-theme-vscode";
import { EditorState } from "@codemirror/state";
import { IHightlighType } from "./Quiz/types";
import EditorDots from "../components/EditorDots";
import EditorControls from "../components/EditorControls";
import IFrame from "../components/Iframe";
import {
  createHighlightPlugin,
  LineHighlight,
} from "../../utils/editorPlugins";
import EditorExplanation from "../components/EditorExplanation";
import { HighlightNumberType } from "./PythonCodeEditor";

export type IWebEditorProps = {
  htmlCode: string;
  cssCode?: string;
  jsCode?: string;
  onOutput?: (output: string) => void;
  onRun?: (code: string) => void;
  onChange?: (code: string) => void;
  hightlight?: IHightlighType;
  initialTab?: "html" | "css" | "js";
  mode: "try" | "view" | "reveal" | "reveal-try";
  htmlCorrectCode?: string;
  cssCorrectCode?: string;
  jsCorrectCode?: string;
  noDots?: boolean;
  noLineNumbers?: boolean;
  width?: string;
  height?: string;
  htmlExplanation?: HighlightNumberType[];
  cssExplanation?: HighlightNumberType[];
  jsExplanation?: HighlightNumberType[];
};

function HtmlCodeEditor({
  htmlCode,
  cssCode = "",
  jsCode = "",
  onOutput,
  hightlight = "neutral",
  mode = "try",
  htmlCorrectCode,
  cssCorrectCode,
  jsCorrectCode,
  noDots = false,
  noLineNumbers = false,
  initialTab,
  width = "100%",
  htmlExplanation,
  cssExplanation,
  jsExplanation,
  height,
  ...props
}: IWebEditorProps) {
  const [html, setHtmlCode] = useState(htmlCode);
  const [css, setCssCode] = useState(cssCode);
  const [js, setJsCode] = useState(jsCode);
  const [output, setOutput] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const [currentTab, setCurrentTab] = useState<"html" | "css" | "js">(
    () => initialTab ?? "html"
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [currentHighlightIndex, setCurrentHighlightIndex] = useState(0);

  const currentTabHighlights =
    currentTab === "html"
      ? htmlExplanation
      : currentTab === "css"
      ? cssExplanation
      : jsExplanation;

  const getCurrentHighlight = useCallback((): LineHighlight | null => {
    if (!htmlExplanation && !cssExplanation && !jsExplanation) return null;

    if (
      !currentTabHighlights ||
      !currentTabHighlights[currentHighlightIndex]?.highlightLines
    )
      return null;

    return {
      lines: currentTabHighlights[currentHighlightIndex].highlightLines,
      lineStyle: hightlight,
      markStyle: hightlight,
    };
  }, [
    currentTab,
    currentHighlightIndex,
    htmlExplanation,
    cssExplanation,
    jsExplanation,
  ]);

  const hasHighlights =
    (currentTab === "html" && htmlExplanation && htmlExplanation.length > 0) ||
    (currentTab === "css" && cssExplanation && cssExplanation?.length > 0) ||
    (currentTab === "js" && jsExplanation && jsExplanation.length > 0);

  const currentHighlight =
    currentTab === "html"
      ? htmlExplanation?.[currentHighlightIndex]
      : currentTab === "css"
      ? cssExplanation?.[currentHighlightIndex]
      : jsExplanation?.[currentHighlightIndex];

  const highlightPlugin = createHighlightPlugin(getCurrentHighlight);

  useEffect(() => {
    if (mode === "reveal" || mode === "reveal-try") {
      setHtmlCode(htmlCorrectCode || htmlCode);
      setCssCode(cssCorrectCode || cssCode);
      setJsCode(jsCorrectCode || jsCode);
    }
  }, [
    mode,
    htmlCorrectCode,
    cssCorrectCode,
    jsCorrectCode,
    htmlCode,
    cssCode,
    jsCode,
  ]);

  useEffect(() => {
    if (onOutput) {
      onOutput(output);
    }
  }, [output, onOutput]);

  const onChange = useCallback(
    (val: string) => {
      props.onChange?.(val);

      if (currentTab === "html") {
        setHtmlCode(val);
      } else if (currentTab === "css") {
        setCssCode(val);
      } else {
        setJsCode(val);
      }
    },
    [currentTab, props]
  );

  const defaultStyle = ` * {
      font-size: 1rem;
      font-family: 'Open Sans', sans-serif;
    }
    body {
      background-color: white;
      margin: 0;
      color: black;
    }
    h1 {
      font-size: 2rem;
    }
    h2 {
      font-size: 1.75rem;
    }
    h3 {
      font-size: 1.5rem;
    }
    h4 {
      font-size: 1.25rem;
    }
    h5 {
      font-size: 1.125rem;
    }
    h6 {
      font-size: 1rem;
    }`;

  const runCode = () => {
    setIsRunning(true);

    let updatedContent = html;

    // Append CSS inside <style> tag or add <style> if not exists
    if (/<style>/s.test(updatedContent)) {
      updatedContent = updatedContent.replace(
        /<style>(.*?)<\/style>/s,
        `<style>${defaultStyle}$1${css}</style>`
      );
    } else {
      updatedContent = updatedContent.replace(
        /<\/head>/,
        `<style>${defaultStyle}${css}</style></head>`
      );
    }

    // Append JS inside <script> tag or add <script> if not exists
    if (/<script>/s.test(updatedContent)) {
      updatedContent = updatedContent.replace(
        /<script>(.*?)<\/script>/s,
        `<script>$1${js}</script>`
      );
    } else {
      updatedContent = updatedContent.replace(
        "</body>",
        `<script>${js}</script></body>`
      );
    }

    // Set the final updated HTML content
    setOutput(updatedContent);

    setIsRunning(false);
    setIsDrawerOpen(true);
  };
  const handlePrevious = () => {
    if (currentHighlightIndex > 0) {
      setCurrentHighlightIndex(currentHighlightIndex - 1);
    }
  };

  const handleNext = () => {
    if (
      currentTabHighlights &&
      currentHighlightIndex < currentTabHighlights.length - 1
    ) {
      setCurrentHighlightIndex(currentHighlightIndex + 1);
    }
  };

  const isResetDisabled = () => {
    if (isRunning) return true;
    if (mode === "reveal-try") {
      return (
        (currentTab === "html" && html === htmlCorrectCode) ||
        (currentTab === "css" && css === cssCorrectCode) ||
        (currentTab === "js" && js === jsCorrectCode)
      );
    }
    return false;
  };

  const handleReset = () => {
    if (mode === "reveal-try") {
      setHtmlCode(htmlCorrectCode || htmlCode);
      setCssCode(cssCorrectCode || cssCode);
      setJsCode(jsCorrectCode || jsCode);
    } else {
      setHtmlCode(htmlCode);
      setCssCode(cssCode);
      setJsCode(jsCode);
    }
    setOutput("");
  };

  const handleTabChange = (type: "html" | "css" | "js") => {
    setCurrentTab(type);
    setCurrentHighlightIndex(0);
  };

  const renderOutput = () => {
    return (
      <>
        {isDrawerOpen && (
          <>
            <Box bg={"neutral.950"}>
              {(mode === "try" ||
                mode === "reveal-try" ||
                mode === "reveal") && (
                <Box
                  pt="0.5rem"
                  height="29rem"
                  border={hightlight !== "neutral" ? "1px solid" : "none"}
                  borderColor={
                    hightlight === "neutral"
                      ? "neutral.800"
                      : hightlight === "success"
                      ? "green.500"
                      : "yellow.600"
                  }
                >
                  <Text
                    mb=".5rem"
                    bg="whiteAlpha.300"
                    color="whiteAlpha.800"
                    borderRadius="full"
                    fontSize="sm"
                  >
                    https://example.com
                  </Text>
                  <IFrame srcDoc={output} height="100%" />
                </Box>
              )}
            </Box>
          </>
        )}
      </>
    );
  };

  return (
    <Box my="1.5rem" px="1rem">
      <Box
        border="1px solid"
        borderColor="neutral.900"
        borderTopRadius=".5rem"
        bg="#1E1E1E"
        p=".5rem"
        width={width}
      >
        {!noDots && (
          <HStack justifyContent={"space-between"}>
            <Text>
              {currentTab === "html"
                ? ".html"
                : currentTab === "css"
                ? ".css"
                : ".js"}
            </Text>
            <EditorDots />
          </HStack>
        )}

        <CodeMirror
          value={currentTab === "html" ? html : currentTab === "css" ? css : js}
          style={{ fontSize: isMobile ? ".9rem" : "1rem" }}
          minHeight={height ?? "5rem"}
          readOnly={mode === "reveal" || mode === "view"}
          extensions={[
            currentTab === "html"
              ? HtmlLang()
              : currentTab === "css"
              ? CssLang()
              : JsLang(),
            highlightPlugin,
            EditorView.lineWrapping,
            ...(mode === "reveal" || mode === "view"
              ? [
                  EditorState.readOnly.of(true),
                  EditorView.editable.of(false),
                  EditorView.theme({
                    ".cm-activeLine": {
                      backgroundColor: "transparent !important",
                    },
                    ".cm-cursor": { display: "none" },
                  }),
                ]
              : []),
          ]}
          onChange={onChange}
          theme={vscodeDark}
          basicSetup={{
            lineNumbers: noLineNumbers ? false : true,
            highlightActiveLineGutter: false,
            foldGutter: false,
            dropCursor: false,
            allowMultipleSelections: false,
            indentOnInput: false,
            bracketMatching: false,
            closeBrackets: false,
            autocompletion: false,
            rectangularSelection: false,
            crosshairCursor: false,
            lintKeymap: false,
          }}
        />
      </Box>

      <Box
        border="1px solid"
        borderColor="neutral.800"
        borderBottomRadius=".5rem"
        borderTopColor="transparent"
      >
        {(mode === "try" || mode === "reveal-try") && (
          <EditorControls
            code=""
            language=""
            noViewMore
            isRunning={isRunning}
            RunOnClick={runCode}
            ResetOnClick={handleReset}
            crowded={isMobile}
            isResetDisabled={isResetDisabled()}
            CenterElement={
              <ButtonGroup size={"sm"}>
                {(cssCode || jsCode) && (
                  <Button
                    variant={currentTab === "html" ? "secondary" : "outline"}
                    onClick={() => handleTabChange("html")}
                  >
                    html
                  </Button>
                )}
                {cssCode && (
                  <Button
                    variant={currentTab === "css" ? "secondary" : "outline"}
                    onClick={() => handleTabChange("css")}
                  >
                    css
                  </Button>
                )}
                {jsCode && (
                  <Button
                    variant={currentTab === "js" ? "secondary" : "outline"}
                    onClick={() => handleTabChange("js")}
                  >
                    js
                  </Button>
                )}
              </ButtonGroup>
            }
          />
        )}
      </Box>

      {hasHighlights && (
        <EditorExplanation
          explanation={currentHighlight as any}
          currentIdx={currentHighlightIndex}
          onPrevClick={handlePrevious}
          onPrevIsDisabled={!hasHighlights || currentHighlightIndex === 0}
          onNextClick={handleNext}
          onNextIsDisabled={
            !hasHighlights ||
            currentHighlightIndex === (currentTabHighlights?.length ?? 0) - 1
          }
        />
      )}

      {/* {ReactDOM.createPortal(
        <Box
          position="fixed"
          bottom={0}
          border={isDrawerOpen ? "1px solid" : "none"}
          borderColor={"whiteAlpha.400"}
          width={isDrawerOpen ? "30%" : 0}
          maxW={"35rem"}
          height="90vh"
          top={10}
          right={10}
          bg={"black"}
          overflow="hidden"
          px={isDrawerOpen ? "1rem" : "0"}
          py={isDrawerOpen ? "0.5rem" : "0"}
          borderRadius={"1rem"}
          transition="width 0.3s ease-in-out"
        >
          {renderOutput()}
        </Box>,
        document.body
      )} */}

      <Box px={"1rem"} py={"1rem"}>
        <Drawer
          isOpen={isDrawerOpen}
          placement="right"
          closeOnOverlayClick
          onClose={() => setIsDrawerOpen(false)}
          size={"md"}
        >
          <DrawerOverlay />
          <DrawerContent bg={"neutral.950"}>
            <DrawerCloseButton size={{ base: "md", lg: "lg" }} />
            <DrawerBody bg={"neutral.950"}>
              <Box mt={"2.2rem"}>{renderOutput()}</Box>
            </DrawerBody>
            <DrawerFooter>
              <Button
                variant="secondary"
                size={"lg"}
                mr={{ base: 0, md: "3" }}
                width={{ base: "full", md: "fit-content" }}
                onClick={() => setIsDrawerOpen(false)}
              >
                Back to lesson
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </Box>
    </Box>
  );
}

export default HtmlCodeEditor;
